import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import type { MediaFile } from "@/types";

interface CloudinaryUploadResponse {
  asset_id: string;
  public_id: string;
  secure_url: string;
  bytes: number;
  format: string;
  resource_type: "image" | "video" | "raw" | "auto";
  width?: number;
  height?: number;
  original_filename?: string;
}

const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;

const deleteCloudinaryMedia = httpsCallable<
  { publicId: string; resourceType?: string },
  { result: string }
>(functions, "deleteCloudinaryMedia");

function ensureCloudinaryConfig() {
  if (!cloudName || !uploadPreset) {
    throw new Error("Cloudinary n'est pas configuré. Renseignez VITE_CLOUDINARY_CLOUD_NAME et VITE_CLOUDINARY_UPLOAD_PRESET.");
  }
}

function buildUploadEndpoint(resourceType: "image" | "video" | "auto") {
  return `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`;
}

function inferResourceType(file: File): "image" | "video" | "auto" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "auto";
}

async function parseUploadError(response: Response): Promise<string> {
  try {
    const payload = await response.json();
    return payload?.error?.message || "Erreur Cloudinary";
  } catch {
    return "Erreur Cloudinary";
  }
}

export const cloudinaryService = {
  async uploadFile(file: File, folder: string, tags: string[] = []): Promise<Omit<MediaFile, "id">> {
    ensureCloudinaryConfig();

    const resourceType = inferResourceType(file);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", uploadPreset);
    formData.append("asset_folder", folder);

    if (tags.length > 0) {
      formData.append("tags", tags.join(","));
    }

    const response = await fetch(buildUploadEndpoint(resourceType), {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(await parseUploadError(response));
    }

    const result = (await response.json()) as CloudinaryUploadResponse;

    return {
      name: file.name,
      url: result.secure_url,
      path: `${folder}/${result.public_id.split("/").pop() || file.name}`,
      publicId: result.public_id,
      assetId: result.asset_id,
      resourceType: result.resource_type,
      format: result.format,
      size: result.bytes,
      type: file.type,
      folder,
      dimensions:
        typeof result.width === "number" && typeof result.height === "number"
          ? { width: result.width, height: result.height }
          : undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
      active: true,
    };
  },

  async deleteFile(media: Pick<MediaFile, "publicId" | "resourceType">): Promise<void> {
    if (!media.publicId) {
      throw new Error("Le média ne possède pas de publicId Cloudinary.");
    }

    await deleteCloudinaryMedia({
      publicId: media.publicId,
      resourceType: media.resourceType,
    });
  },
};
